import { Injectable } from '@angular/core';
import { ValidatorFn, Validators, AbstractControl  } from '@angular/forms';
import { BrowserStack } from 'protractor/built/driverProviders';
import * as jsonPattern from '../../../../assets/validators/pattern.json';
import { DateValidator } from './DateValidator';
import { LogService } from './log.service';

export function existsInReferenceListValidator(getOptions: () => Array<{ code: string }>): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      // Champ vide : laissé aux validateurs 'required'/'pattern' existants, pas à celui-ci
      return null;
    }
    const options = getOptions ? getOptions() : [];
    if (!options || options.length === 0) {
      // Liste pas encore chargée (ou volontairement vide) : ne pas bloquer artificiellement
      // l'utilisateur avec une erreur due à un simple problème de timing de chargement
      return null;
    }
    const exists = options.some(o => o?.code === value);
    return exists ? null : { invalidReferenceValue: true };
  };
}

export function minCaractereValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value || !Array.isArray(control.value)) {
      return null;
    }

    const invalidItems = control.value.filter((item: string) => item.trim().length < minLength);
    return invalidItems.length > 0 ? { 'minCaractere': { value: control.value, requiredLength: minLength } } : null;
  };
}
/*PRB0053517: implémenter la logique du nouveau contrôle*/
export function minTextLengthValidator(minTextLength: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    return control.value.trim().length <  minTextLength ? { 'minTextLength': { value: control.value, requiredLength: minTextLength } } : null;
  };
}

export function maxCaractereValidator(maxLength: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value || !Array.isArray(control.value)) {
      return null;
    }

    const invalidItems = control.value.filter((item: string) => item.trim().length > maxLength);
    return invalidItems.length > 0 ? { 'maxCaractere': { value: control.value, requiredLength: maxLength } } : null;
  };
}

export function arrayPatternValidator(pattern: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // Si la valeur est nulle ou undefined, pas d'erreur
    if (control.value == null) {
      return null;
    }

    // Si c'est un tableau
    if (Array.isArray(control.value)) {
      // Si le tableau est vide, pas d'erreur (champ optionnel)
      if (control.value.length === 0) {
        return null;
      }
      // Valider chaque élément du tableau
      const regex = new RegExp('^' + pattern + '$');
      const invalidItems = control.value.filter((item: string) => {
        // Vérifier que l'élément est une chaîne et qu'il ne correspond pas au pattern
        return typeof item === 'string' && !regex.test(item);
      });

      if (invalidItems.length > 0) {
        return { 'pattern': { requiredPattern: '^' + pattern + '$', actualValue: control.value } };
      }
      return null;
    }

    // Si ce n'est pas un tableau, utiliser la validation standard
    const regex = new RegExp('^' + pattern + '$');
    if (regex.test(control.value)) {
      return null;
    }

    return { 'pattern': { requiredPattern: '^' + pattern + '$', actualValue: control.value } };
  };
}

@Injectable({
  providedIn: 'root',
})
export class ValidatorService {

  private LOG: LogService = new LogService(ValidatorService);
  constructor() { }

  public getValidator(rules: string): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    this.LOG.debug('FIX BUILD', rules);
    if(!rules){
      return validators;
    }
    rules.split('|').forEach((rule) => {
      switch (rule.split('_')[0]) {
        case 'maxSize':
          validators.push(Validators.maxLength(parseInt(rule.split('_')[1])));
          break;
        case 'minSize':
          validators.push(Validators.minLength(parseInt(rule.split('_')[1])));
          break;
        case 'pattern':
          const patternRegex = jsonPattern.patterns[parseInt(rule.split('_')[1]) - 1].regexp;
          // Validateur personnalisé qui gère les tableaux vides
          validators.push((control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value;

            // Si la valeur est null ou undefined, pas d'erreur
            if (!value) {
              return null;
            }

            // Si c'est un tableau
            if (Array.isArray(value)) {
              // Filtrer les éléments vides (chaînes vides ou nulls)
              const nonEmptyItems = value.filter((item: any) => {
                if (typeof item === 'string') {
                  return item.trim() !== '';
                }
                return item != null;
              });

              // Si le tableau ne contient que des éléments vides, pas d'erreur (champ optionnel)
              if (nonEmptyItems.length === 0) {
                return null;
              }

              // Valider chaque élément non vide
              const regex = new RegExp('^' + patternRegex + '$');
              const hasInvalid = nonEmptyItems.some((item: any) => {
                // Si c'est un objet avec un code, tester le code
                const testValue = (item && typeof item === 'object' && item.code) ? item.code : item;
                return typeof testValue === 'string' && !regex.test(testValue);
              });

              return hasInvalid ? { 'pattern': { requiredPattern: '^' + patternRegex + '$', actualValue: value } } : null;
            }

            // Pour les valeurs simples (non tableaux)
            // Si c'est une chaîne vide, pas d'erreur (champ optionnel)
            if (typeof value === 'string' && value.trim() === '') {
              return null;
            }

						// Sinon, utiliser la validation standard
						return Validators.pattern(patternRegex)(control);
					});
					break;
				case 'min':
					validators.push(Validators.min(parseInt(rule.split('_')[1])));
					break;
				case 'max':
					validators.push(Validators.max(parseInt(rule.split('_')[1])));
					break;
				case 'digits':
					validators.push(Validators.pattern('(-)?[0-9]+(\\.[0-9]{1,2})?'));
					validators.push(Validators.min(0));
					break;
				case 'required':
					validators.push(Validators.required);
					break;
				case 'requiredDate':
					validators.push(Validators.compose([Validators.required, DateValidator.date]));
					break;
				case 'dateMin':
					validators.push()
					break;
				case 'dateMax':
					validators.push()
					break;
				case 'minCaractere':
					const minLength = parseInt(rule.split('_')[1]);
					validators.push(minCaractereValidator(minLength));
					break;
        case 'minTextLength': /*PRB0053517: active le nouveau contrôle*/
          const minTextLength = parseInt(rule.split('_')[1]);
          validators.push(minTextLengthValidator(minTextLength));
          break;
				case 'maxCaractere':
					const maxLength = parseInt(rule.split('_')[1]);
					validators.push(maxCaractereValidator(maxLength));
					break;
				default:
					break;
			}
		});
		return validators;
	}

  getNoRules(rules: string): number{
    let nb=0;
    jsonPattern.patterns.forEach(pattern => {
      if(pattern.regexp === rules){
        nb = pattern.id;
      }
    });
    return nb;
  }



}
